import React from 'react'
import {View} from 'react-native'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {getCounts, getPostImages} from '#/lib/feed/mediaHelpers'
import {
  type CommonNavigatorParams,
  type NavigationProp,
} from '#/lib/routes/types'
import {usePostThreadQuery} from '#/state/queries/post-thread'
import {useSavedCollections} from '#/state/savedCollections'
import {useAgent} from '#/state/session'
import {atoms as a, select, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import PostThreadComments from '#/components/comments/PostThreadComments'
import GalleryMedia from '#/components/feed/GalleryMedia'
import {ArrowLeft_Stroke2_Corner0_Rounded as ArrowLeft} from '#/components/icons/Arrow'
import * as Layout from '#/components/Layout'
import PostActions from '#/components/PostActions'
import SaveToCollectionSheet from '#/components/SaveToCollectionSheet'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'GalleryPost'>

type ReactionState = {liked: boolean; saved: boolean; likeUri?: string}
type CountState = {likes: number; comments: number}

export default function GalleryPostScreen({route}: Props) {
  const t = useTheme()
  const {gtMobile, gtTablet} = useBreakpoints()
  const navigation = useNavigation<NavigationProp>()
  const agent = useAgent()
  const [activeSave, setActiveSave] = React.useState<string | null>(null)
  const [reaction, setReaction] = React.useState<ReactionState>({
    liked: false,
    saved: false,
  })
  const [counts, setCounts] = React.useState<CountState>({
    likes: 0,
    comments: 0,
  })
  const composerRef = React.useRef<HTMLTextAreaElement>(null)

  const uri = React.useMemo(() => {
    if (route.params?.uri) return route.params.uri
    if (route.params?.uriEncoded) {
      try {
        return decodeURIComponent(route.params.uriEncoded)
      } catch {
        return route.params.uriEncoded
      }
    }
    return ''
  }, [route.params])

  const isMissingUri = !uri

  const {data, isLoading, isError} = usePostThreadQuery(uri)
  const post = React.useMemo(() => {
    const node = data?.thread
    if (!node) return null
    if (node.type === 'post') return node.post
    return null
  }, [data])

  React.useEffect(() => {
    if (!post) return
    const countsRaw = getCounts(post as any)
    setCounts({
      likes: countsRaw.like ?? post.likeCount ?? 0,
      comments: countsRaw.comment ?? post.replyCount ?? 0,
    })
    setReaction({
      liked: Boolean(post.viewer?.like),
      saved: Boolean(post.viewer?.bookmarked),
      likeUri: post.viewer?.like ?? undefined,
    })
  }, [post])

  const handleToggleLike = React.useCallback(async () => {
    if (!post?.uri || !post.cid) return
    const previousReaction = reaction
    const previousCounts = counts
    const nextLiked = !reaction.liked

    setReaction(current => ({
      ...current,
      liked: nextLiked,
      likeUri: nextLiked ? current.likeUri : undefined,
    }))
    setCounts(current => ({
      ...current,
      likes: Math.max(0, current.likes + (nextLiked ? 1 : -1)),
    }))

    try {
      if (nextLiked) {
        const res = await agent.like(post.uri, post.cid)
        setReaction(current => ({
          ...current,
          liked: true,
          likeUri: res.uri,
        }))
      } else {
        const likeUri = reaction.likeUri ?? post.viewer?.like
        if (likeUri) {
          await agent.deleteLike(likeUri)
        }
        setReaction(current => ({
          ...current,
          liked: false,
          likeUri: undefined,
        }))
      }
    } catch (err) {
      console.error('[GalleryPost] like error', err)
      setReaction(previousReaction)
      setCounts(previousCounts)
    }
  }, [agent, counts, post, reaction])

  const handleShare = React.useCallback(() => {
    if (!post) return
    const url = `https://tellus.app/profile/${post.author?.handle}/post/${post.uri.split('/').pop()}`
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({url}).catch(() => {
        if (typeof window !== 'undefined') window.open(url, '_blank')
      })
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {})
    } else if (typeof window !== 'undefined') {
      window.open(url, '_blank')
    }
  }, [post])

  const handleSave = React.useCallback(() => {
    if (!post?.uri) return
    setActiveSave(post.uri)
  }, [post?.uri])

  const handleCloseSave = React.useCallback(() => {
    if (!post?.uri) {
      setActiveSave(null)
      return
    }
    const membership = useSavedCollections.getState().contains(post.uri).inAny
    setReaction(current => ({...current, saved: membership}))
    setActiveSave(null)
  }, [post?.uri])

  const handleCommentCountChange = React.useCallback((delta: number) => {
    setCounts(current => ({
      ...current,
      comments: Math.max(0, current.comments + delta),
    }))
  }, [])

  const handleFocusComposer = React.useCallback(() => {
    composerRef.current?.focus()
  }, [])

  const handleBack = React.useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.navigate('Home')
    }
  }, [navigation])

  const glassBackground = select(t.name, {
    light: 'rgba(255,255,255,0.65)',
    dark: 'rgba(18,18,18,0.55)',
    dim: 'rgba(18,18,18,0.55)',
  })
  const glassBorder = select(t.name, {
    light: 'rgba(0,0,0,0.06)',
    dark: 'rgba(255,255,255,0.08)',
    dim: 'rgba(255,255,255,0.08)',
  })
  const glassShadow = select(t.name, {
    light: '0 8px 24px rgba(0,0,0,0.06)',
    dark: 'none',
    dim: 'none',
  })
  const maxWidth = gtTablet ? 600 : gtMobile ? 720 : '100%'
  const horizontalPadding = gtMobile ? 16 : 8

  const images = post ? getPostImages(post) : []
  const record = post?.record as {text?: string} | undefined
  const text = record?.text ?? ''
  const authorName =
    post?.author?.displayName || post?.author?.handle || 'Auteur'
  const authorHandle = post?.author?.handle
  const createdAt = post?.indexedAt ? new Date(post.indexedAt) : null

  return (
    <Layout.Screen>
      <div className="gallery-post-screen">
        <div
          className="gallery-post-header"
          style={{
            backgroundColor: glassBackground,
            borderBottomColor: glassBorder,
            boxShadow: glassShadow,
          }}>
          <div
            className="gallery-post-header-inner"
            style={{
              maxWidth,
              paddingLeft: horizontalPadding,
              paddingRight: horizontalPadding,
            }}>
            <Button
              label="Back"
              size="small"
              variant="ghost"
              color="secondary"
              shape="square"
              onPress={handleBack}
              style={[a.justify_center, {width: 44, height: 44}]}>
              <ButtonIcon icon={ArrowLeft} size="lg" />
            </Button>
            <Text style={[a.font_heavy, {fontSize: 20}, t.atoms.text]}>
              Post
            </Text>
            <View style={{width: 44, height: 44}} />
          </div>
        </div>

        <div
          className="gallery-post-body"
          style={{
            maxWidth,
            paddingLeft: horizontalPadding,
            paddingRight: horizontalPadding,
          }}>
          {isLoading && (
            <div className="gallery-post-loading">
              <Text>Chargement…</Text>
            </div>
          )}
          {isMissingUri && (
            <div className="gallery-post-error">
              <Text>Post introuvable.</Text>
            </div>
          )}
          {isError && !post && !isMissingUri && (
            <div className="gallery-post-error">
              <Text>Impossible de charger ce post.</Text>
            </div>
          )}
          {post && (
            <article className="gallery-post-card">
              <div className="insta-header">
                <div
                  className="insta-avatar"
                  style={
                    post.author?.avatar
                      ? {backgroundImage: `url(${post.author.avatar})`}
                      : undefined
                  }>
                  {!post.author?.avatar && (
                    <Text style={{fontSize: 16, color: 'inherit'}}>
                      {authorName[0]?.toUpperCase() ?? '•'}
                    </Text>
                  )}
                </div>
                <div className="insta-header-text">
                  <Text style={{fontSize: 15, fontWeight: '600'}}>
                    {authorName}
                  </Text>
                  {authorHandle && (
                    <Text style={{fontSize: 12, opacity: 0.6}}>
                      @{authorHandle}
                    </Text>
                  )}
                </div>
                <button
                  type="button"
                  className="insta-menu"
                  aria-label="Options">
                  <Text style={{fontSize: 20}}>•••</Text>
                </button>
              </div>
              {images.length > 0 ? (
                <GalleryMedia images={images} />
              ) : (
                <div className="gallery-post-placeholder">
                  <Text>Aucun media disponible.</Text>
                </div>
              )}
              <div className="insta-actions">
                <PostActions
                  layout="gallery"
                  onLike={handleToggleLike}
                  onComment={handleFocusComposer}
                  onSave={handleSave}
                  onShare={handleShare}
                  liked={reaction.liked}
                  saved={reaction.saved}
                  counts={counts}
                />
              </div>
              <div className="insta-meta">
                <div className="insta-likes">
                  <Text style={{fontSize: 14, fontWeight: '600'}}>
                    {counts.likes === 1 ? '1 like' : `${counts.likes} likes`}
                  </Text>
                </div>
                {text && (
                  <Text style={{fontSize: 14, lineHeight: 1.45}}>
                    <Text style={{fontWeight: '600'}}>{authorName}</Text>{' '}
                    <Text>{text}</Text>
                  </Text>
                )}
                {createdAt && (
                  <Text style={{fontSize: 12, opacity: 0.6}}>
                    {createdAt.toLocaleDateString()}
                  </Text>
                )}
              </div>
              <div className="gallery-post-comments">
                <PostThreadComments
                  post={post}
                  open
                  variant="detail"
                  composerRef={composerRef}
                  onCommentCountChange={handleCommentCountChange}
                />
              </div>
            </article>
          )}
        </div>
      </div>

      {activeSave && (
        <SaveToCollectionSheet atUri={activeSave} onClose={handleCloseSave} />
      )}
    </Layout.Screen>
  )
}
